import React, { forwardRef, ReactNode, ChangeEvent, memo } from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import clsx from 'clsx';
import MenuItem, { MenuItemProps } from '@material-ui/core/MenuItem';
import Select, { SelectProps } from '@material-ui/core/Select';
import InputLabel, {
  InputLabelProps as MuiInputLabelProps,
} from '@material-ui/core/InputLabel';
import Checkbox from '@material-ui/core/Checkbox';
import ListItemText from '@material-ui/core/ListItemText';
import FormControl from '@material-ui/core/FormControl';

const useStyles = makeStyles((theme) => ({
  selectRoot: {
    borderRadius: 25,
    backgroundColor: theme.palette.common.white,
    textAlign: 'left',
    '& > .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF6E2E',
    },
    '&:hover > .MuiOutlinedInput-notchedOutline': {
      borderColor: '#ed936b',
    },
    '& > .MuiInputBase-input:focus': {
      borderRadius: 25,
    },
    [theme.breakpoints.up('sm')]: {
      minWidth: 230,
    },
  },
  inputLabel: {
    display: 'inline-flex',
    '&:MuiInputLabel-shrink': {
      textIndent: '0px',
    },
  },
  inputLabelIcon: {
    width: '30px',
    height: '5%',
    padding: '0px 10px 0 5px',
    [theme.breakpoints.down('xs')]: {
      width: '40%',
      '&, _::-webkit-full-page-media, _:future, :root .icon': {
        width: '30px',
      },
    },
  },
  menuItem: {
    whiteSpace: 'normal',
    wordBreak: 'break-all',
    overflowWrap: 'break-word',
  },
}));

export const FilterSelectIcon = ({
  src,
  className,
  alt,
}: {
  src: string;
  className?: string;
  alt: string;
}) => {
  const classes = useStyles();

  return (
    <img
      src={src}
      className={clsx(classes.inputLabelIcon, className)}
      alt={alt}
    />
  );
};

type FilterSelectMenuItemProps = Omit<MenuItemProps, 'button'> & {
  multiple: boolean | undefined;
  children: ReactNode;
  checked?: boolean;
};

const FilterSelectMenuItem = memo(
  ({ multiple, children, checked, ...props }: FilterSelectMenuItemProps) => (
    <MenuItem {...props}>
      {multiple ? (
        <>
          <Checkbox checked={checked} />
          <ListItemText primary={children} />
        </>
      ) : (
        children
      )}
    </MenuItem>
  ),
);

export type FilterSelectProps = Pick<
  SelectProps,
  'label' | 'name' | 'value' | 'onClose' | 'multiple'
> & {
  onChange: (
    event: ChangeEvent<{ name?: string; value: any }> | { [name: string]: any },
  ) => void;
  choices?: Array<{
    text: ReactNode;
    value: any;
    itemProps?: Omit<MenuItemProps, 'button'>;
  }>;
  InputLabelProps?: MuiInputLabelProps;
};

const FilterSelect = forwardRef(
  (
    {
      choices,
      label,
      name,
      value,
      onChange,
      multiple,
      onClose,
      InputLabelProps,
    }: FilterSelectProps,
    ref,
  ) => {
    const classes = useStyles();

    return (
      <FormControl variant="outlined" size="small" fullWidth>
        <InputLabel
          {...InputLabelProps}
          className={clsx(classes.inputLabel, InputLabelProps?.className)}
        >
          {label}
        </InputLabel>
        <Select
          onChange={(event) =>
            name
              ? onChange?.({ [name]: event.target.value })
              : onChange?.(event)
          }
          className={classes.selectRoot}
          label={label}
          value={value != null ? value : ''}
          fullWidth
          name={name}
          onClose={onClose}
          multiple={multiple}
          {...(multiple && {
            renderValue: (selected: Array<any>) =>
              selected
                .map((s) => {
                  const f = choices?.find?.(({ value }) => s === value);
                  return f?.text;
                })
                .join(', '),
          })}
          ref={ref}
        >
          {!multiple && (
            <MenuItem value="">
              <em style={{ visibility: 'hidden' }}>-</em>
            </MenuItem>
          )}
          {choices?.map?.(({ text, value: itemValue, itemProps }, index) => (
            <FilterSelectMenuItem
              {...itemProps}
              key={index}
              value={itemValue}
              className={clsx(classes.menuItem, itemProps?.className)}
              multiple={multiple}
              {...(multiple && {
                checked: (value as Array<any>).indexOf(itemValue) > -1,
              })}
            >
              {text}
            </FilterSelectMenuItem>
          ))}
        </Select>
      </FormControl>
    );
  },
);

export default FilterSelect;
